/**
 * Mapping from WeChat emoticon tokens (e.g. "[微笑]", "[Smile]") to
 * Unicode emoji codepoints so message bodies containing those tokens
 * render visually in the chat bubble.
 *
 * Covers the standard WeChat system-emoticon set (both Chinese and the
 * English aliases some payloads carry). Anything not in the table is
 * passed through unchanged so arbitrary "[xxx]" text doesn't get lost.
 */

const emoticons: Record<string, string> = {
    // Faces
    '[微笑]': '🙂',
    '[Smile]': '🙂',
    '[撇嘴]': '😒',
    '[Grimace]': '😒',
    '[色]': '😍',
    '[Drool]': '😍',
    '[发呆]': '😳',
    '[Scowl]': '😳',
    '[得意]': '😎',
    '[CoolGuy]': '😎',
    '[流泪]': '😭',
    '[Sob]': '😭',
    '[害羞]': '☺️',
    '[Shy]': '☺️',
    '[闭嘴]': '🤐',
    '[Silent]': '🤐',
    '[睡]': '😴',
    '[Sleep]': '😴',
    '[大哭]': '😭',
    '[Cry]': '😭',
    '[尴尬]': '😅',
    '[Awkward]': '😅',
    '[发怒]': '😠',
    '[Angry]': '😠',
    '[调皮]': '😜',
    '[Tongue]': '😜',
    '[呲牙]': '😁',
    '[Grin]': '😁',
    '[惊讶]': '😲',
    '[Surprised]': '😲',
    '[难过]': '😔',
    '[Frown]': '😔',
    '[酷]': '😎',
    '[Ruthless]': '😎',
    '[冷汗]': '😰',
    '[Sweats]': '😰',
    '[抓狂]': '🤯',
    '[Crazy]': '🤯',
    '[吐]': '🤮',
    '[Puke]': '🤮',
    '[偷笑]': '🤭',
    '[Chuckle]': '🤭',
    '[愉快]': '😊',
    '[Joyful]': '😊',
    '[白眼]': '🙄',
    '[Slight]': '🙄',
    '[傲慢]': '😏',
    '[Smug]': '😏',
    '[困]': '😪',
    '[Drowsy]': '😪',
    '[惊恐]': '😱',
    '[Panic]': '😱',
    '[流汗]': '😓',
    '[Sweat]': '😓',
    '[憨笑]': '😄',
    '[Laugh]': '😄',
    '[悠闲]': '😌',
    '[Loafer]': '😌',
    '[咒骂]': '🤬',
    '[Scold]': '🤬',
    '[疑问]': '❓',
    '[Doubt]': '❓',
    '[嘘]': '🤫',
    '[Shhh]': '🤫',
    '[晕]': '😵',
    '[Dizzy]': '😵',
    '[衰]': '😩',
    '[BadLuck]': '😩',
    '[骷髅]': '💀',
    '[Skull]': '💀',
    '[敲打]': '🔨',
    '[Hammer]': '🔨',
    '[再见]': '👋',
    '[Bye]': '👋',
    '[擦汗]': '😓',
    '[Relief]': '😓',
    '[抠鼻]': '🤪',
    '[DigNose]': '🤪',
    '[鼓掌]': '👏',
    '[Clap]': '👏',
    '[坏笑]': '😏',
    '[Trick]': '😏',
    '[左哼哼]': '😤',
    '[Bah!L]': '😤',
    '[右哼哼]': '😤',
    '[Bah!R]': '😤',
    '[哈欠]': '🥱',
    '[Yawn]': '🥱',
    '[鄙视]': '😒',
    '[Lookdown]': '😒',
    '[委屈]': '🥺',
    '[Wronged]': '🥺',
    '[快哭了]': '😢',
    '[Puling]': '😢',
    '[阴险]': '😏',
    '[Sly]': '😏',
    '[亲亲]': '😘',
    '[Kiss]': '😘',
    '[吓]': '😨',
    '[Wrath]': '😨',
    '[可怜]': '🥺',
    '[Whimper]': '🥺',
    '[菜刀]': '🔪',
    '[Cleaver]': '🔪',
    '[西瓜]': '🍉',
    '[Melon]': '🍉',
    '[啤酒]': '🍺',
    '[Beer]': '🍺',
    '[咖啡]': '☕',
    '[Coffee]': '☕',
    '[猪头]': '🐷',
    '[Pig]': '🐷',
    '[玫瑰]': '🌹',
    '[Rose]': '🌹',
    '[凋谢]': '🥀',
    '[Wilt]': '🥀',
    '[嘴唇]': '💋',
    '[Lips]': '💋',
    '[爱心]': '❤️',
    '[Heart]': '❤️',
    '[心碎]': '💔',
    '[BrokenHeart]': '💔',
    '[蛋糕]': '🎂',
    '[Cake]': '🎂',
    '[炸弹]': '💣',
    '[Bomb]': '💣',
    '[便便]': '💩',
    '[Poop]': '💩',
    '[月亮]': '🌙',
    '[Moon]': '🌙',
    '[太阳]': '☀️',
    '[Sun]': '☀️',
    '[礼物]': '🎁',
    '[Gift]': '🎁',
    '[拥抱]': '🤗',
    '[Hug]': '🤗',
    '[强]': '👍',
    '[Strong]': '👍',
    '[弱]': '👎',
    '[Weak]': '👎',
    '[握手]': '🤝',
    '[ShakeHands]': '🤝',
    '[胜利]': '✌️',
    '[Victory]': '✌️',
    '[抱拳]': '🙏',
    '[Salute]': '🙏',
    '[勾引]': '💁',
    '[Beckon]': '💁',
    '[拳头]': '👊',
    '[Fist]': '👊',
    '[OK]': '👌',
    '[跳跳]': '🕺',
    '[Jump]': '🕺',
    '[发抖]': '🥶',
    '[Shake]': '🥶',
    '[怄火]': '😤',
    '[转圈]': '💫',
    '[Twirl]': '💫',
    // Newer WeChat emoticons
    '[笑脸]': '😁',
    '[生病]': '🤒',
    '[脸红]': '😊',
    '[破涕为笑]': '😂',
    '[吐舌]': '😛',
    '[哈哈]': '🤣',
    '[奸笑]': '😏',
    '[机智]': '🤓',
    '[皱眉]': '😟',
    '[耶]': '✌️',
    '[鬼魂]': '👻',
    '[合十]': '🙏',
    '[强壮]': '💪',
    '[庆祝]': '🎉',
    '[礼炮]': '🎊',
    '[烟花]': '🎆',
    '[福]': '🧧',
    '[红包]': '🧧',
    '[走你]': '🏃',
    '[福到]': '🧧',
    '[笑哭]': '😂',
    '[苦涩]': '😖',
    '[叹气]': '😮\u200d💨',
    '[翻白眼]': '🙄',
    '[666]': '🔥',
    '[裂开]': '🤯',
    '[让我看看]': '👀',
    '[叹]': '😮\u200d💨',
}

// Pre-compile the regex so every render reuses the same engine.
const TOKEN_REGEX = /\[([^\]]{1,12})\]/g

/**
 * Replace every supported `[name]` token in `text` with the matching emoji.
 * Unknown tokens and plain text pass through untouched.
 */
export function replaceWeChatEmoticons(text: string): string {
    if (!text || !text.includes('[')) return text
    return text.replace(TOKEN_REGEX, (match) => emoticons[match] ?? match)
}
